import * as Tone from 'tone';
import { BASE_NOTES, HARMONY, PLANET_GENRES } from '../utils/constants.js';
import { events, EVT } from '../utils/events.js';
import { HarmonySystem } from './HarmonySystem.js';
import { createDrumKit } from './DrumKit.js';
import { createRoleSampler } from './sampleLibrary.js';
import { expandSong } from './songFormat.js';
import {
  ORCHESTRA_SECTIONS,
  INSTRUMENT_SECTION_MAP,
} from '../utils/constants_harmony.js';

/**
 * Audio engine — generates the musical signature of each planet.
 * Each planet has:
 *   - A warm drone/pad (root + fifth + sub-octave)
 *   - Creature call-and-response synths (pooled, event-driven)
 *   - Spatial audio: distance-based volume attenuation
 * Together they form the planet's "symphony."
 */
export class AudioEngine {
  constructor() {
    this.started = false;
    this.masterGain = null;
    this.nearbyGain = null;
    this.currentPlanetSymphony = null;
    this.nearbyPlanetVoices = [];
    this.currentPlanet = null;
    this.reverbNode = null;
    this.delayNode = null;
    this.filterNode = null;
    this.chorusNode = null;
    this.effectGain = null;
    this.footstepSynth = null;
    this.jumpSynth = null;
    this.boostSynth = null;
    this.jetpackSynth = null;
    this.jetpackGain = null;
    this.jetpackFilter = null;
    this.shipEngineSynth = null;
    this.shipEngineGain = null;
    this.shipEngineFilter = null;
    this.scheduledEvents = [];
    this._eventUnsubscribers = [];
    this._initPromise = null;

    /** Harmony system — keeps all sounds in key */
    this.harmony = new HarmonySystem();
    this._footstepIndex = 0;

    /** Instrument voice synths — one per role, shared across all creatures of that type */
    this._instrumentSynths = {};  // roleId → { synth, gain, noiseSynth? }

    /** Player camera position for spatial audio */
    this._playerPos = null;

    /** Max audible distance for creature sounds */
    this._maxHearDist = 80;

    /** Broadcast mode — hear all creatures at once regardless of distance */
    this._broadcastMode = false;
  }

  async init() {
    if (this.started) return;
    if (this._initPromise) return this._initPromise;

    this._initPromise = (async () => {
      try {
        await Tone.start();

        // Master chain with warm reverb + subtle chorus
        this.reverbNode = new Tone.Reverb({ decay: 5, wet: 0.35 }).toDestination();
        this.chorusNode = new Tone.Chorus(0.8, 3.5, 0.25).connect(this.reverbNode);
        this.chorusNode.wet.value = 0.2;
        this.delayNode = new Tone.FeedbackDelay('8n.', 0.15).connect(this.chorusNode);
        this.filterNode = new Tone.Filter(3000, 'lowpass').connect(this.delayNode);
        this.masterGain = new Tone.Gain(0.6).connect(this.filterNode);
        this.nearbyGain = new Tone.Gain(0.4).connect(this.filterNode);

        this._createEffectSynths();
        this._createInstrumentSynths();
        this._bindEventHandlers();

        this.started = true;
        console.log('[Audio] Engine initialized');
      } catch (err) {
        this.started = false;
        console.error('[Audio] Engine failed to initialize:', err);
        throw err;
      } finally {
        this._initPromise = null;
      }
    })();

    return this._initPromise;
  }

  _createPlanetSymphonyGain() {
    return new Tone.Gain(0).connect(this.masterGain);
  }

  _disposePlanetSymphony(symphony) {
    if (!symphony) return;
    if (symphony.drone) {
      try {
        symphony.drone.synth.releaseAll();
        if (symphony.drone.lfo) symphony.drone.lfo.stop();
        setTimeout(() => {
          try { symphony.drone.synth.dispose(); } catch (_) {}
          try { if (symphony.drone.lfo) symphony.drone.lfo.dispose(); } catch (_) {}
        }, 2000);
      } catch (_) {}
    }
    if (symphony.droneOsc) {
      try {
        symphony.droneOsc.stop();
        symphony.droneOsc.dispose();
      } catch (_) {}
    }
    if (symphony.padSynth) {
      try {
        symphony.padSynth.releaseAll();
        setTimeout(() => { try { symphony.padSynth.dispose(); } catch (_) {} }, 2000);
      } catch (_) {}
    }
    if (symphony.padLfo) {
      try { symphony.padLfo.stop(); symphony.padLfo.dispose(); } catch (_) {}
    }
    if (symphony.padFilter) {
      try { setTimeout(() => symphony.padFilter.dispose(), 2000); } catch (_) {}
    }
    symphony.gain.dispose();
  }

  _fadeOutCurrentPlanetSymphony(duration = 1) {
    if (!this.currentPlanetSymphony) return;
    const oldSymphony = this.currentPlanetSymphony;
    oldSymphony.gain.gain.rampTo(0, duration);
    setTimeout(() => {
      if (this.currentPlanetSymphony === oldSymphony) {
        this.currentPlanetSymphony = null;
      }
      this._disposePlanetSymphony(oldSymphony);
    }, (duration + 0.2) * 1000);
  }

  /**
   * Start playing the symphony for a planet.
   * No per-creature synths — all creature sounds come from the
   * pooled call/response synths triggered by FlockingSystem events.
   */
  async startPlanetSymphony(planetData, creatures, options = {}) {
    if (!this.started) await this.init();
    if (!this.masterGain || !this.reverbNode) return;

    const fadeInDuration = options.fadeInDuration ?? 1.5;
    const fadeOutDuration = options.fadeOutDuration ?? 1.0;

    const previousSymphony = this.currentPlanetSymphony;
    this.currentPlanet = planetData;

    const { music } = planetData;
    const rootIdx = BASE_NOTES.indexOf(music.root);

    // Configure harmony system for this planet
    this.harmony.setPlanet(music, planetData.seed);
    this._footstepIndex = 0;
    // Preserve broadcast mode across planet/source switches so Planet Radio
    // keeps working while traveling between planets.

    // Genre-aware effect configuration
    const genre = music.genre || 'orchestral';
    const genreCfg = PLANET_GENRES[genre] || PLANET_GENRES.orchestral;

    // Configure reverb for this planet (genre-influenced)
    this.reverbNode.decay = genreCfg.reverbMix * 8 + 2;
    this.reverbNode.wet.value = genreCfg.reverbMix;

    // Configure filter for genre character
    if (this.filterNode) {
      this.filterNode.frequency.rampTo(genreCfg.filterCutoff || 3000, 0.5);
    }

    // Configure delay for genre (more for dub/electronic, less for jazz/folk)
    if (this.delayNode) {
      const delayWet = genre === 'electronic' ? 0.25
                     : genre === 'ambient' || genre === 'cinematic' ? 0.3
                     : genre === 'breakcore' ? 0.1
                     : genre === 'jazz' ? 0.08
                     : 0.15;
      this.delayNode.wet.value = delayWet;
    }

    const newGain = this._createPlanetSymphonyGain();
    const symphony = {
      gain: newGain,
      drone: null,
      droneOsc: null,
      padSynth: null,
      padLfo: null,
      padFilter: null,
      planet: planetData,
    };

    // ── Planet Drone — warm layered pad (genre-adjusted) ──
    const droneGain = genreCfg.droneLevel || 0.5;
    if (droneGain > 0.05) {
      symphony.drone = this._createDrone(music, rootIdx, newGain, droneGain);
      symphony.droneOsc = this._createPlanetDroneOscillator(symphony.drone.synth, music, rootIdx, newGain, droneGain);
    }

    // ── Warm evolving pad layer (skip for breakcore) ──
    if (genre !== 'breakcore') {
      const padResult = this._createWarmPad(music, rootIdx, newGain, droneGain);
      symphony.padSynth = padResult.synth;
      symphony.padLfo = padResult.lfo;
      symphony.padFilter = padResult.filter;
    }

    // (no per-creature voice loops — all creature audio is event-driven)

    this.currentPlanetSymphony = symphony;
    newGain.gain.rampTo(1, fadeInDuration);

    if (previousSymphony) {
      previousSymphony.gain.gain.rampTo(0, fadeOutDuration);
      setTimeout(() => this._disposePlanetSymphony(previousSymphony), (fadeOutDuration + 0.2) * 1000);
    }

    Tone.getTransport().bpm.value = music.tempo;
    Tone.getTransport().start();

    events.emit(EVT.MUSIC_UPDATE, { planet: planetData.name, scale: music.scale, root: music.root, genre: music.genre, timeSig: music.timeSig });
  }

  _createDrone(music, rootIdx, outputGain = this.masterGain, droneLevel = 0.5) {
    // Rich warm drone pad — root, fifth, and octave below
    const freq = Tone.Frequency(BASE_NOTES[rootIdx] + (music.octave - 1)).toFrequency();
    const subFreq = freq * 0.5; // sub-octave

    // Adjust volume based on genre drone level
    const baseVol = -28 + (droneLevel * 12); // -28 (quiet) to -16 (loud)

    const synth = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: 'sine' },
      envelope: { attack: 4, decay: 2, sustain: 0.7, release: 5 },
      volume: baseVol,
    }).connect(outputGain);

    // Play root, fifth, and sub-octave for warmth
    const fifth = freq * 1.5;
    synth.triggerAttack([subFreq, freq, fifth]);

    // Gentle breathing LFO for organic movement
    const lfo = new Tone.LFO(0.03 + music.drone * 0.05, -8, -3).start();
    lfo.connect(synth.volume);

    return { synth, lfo, type: 'drone' };
  }

  _createPlanetDroneOscillator(droneSynth, music, rootIdx, outputGain = this.masterGain, droneLevel = 0.5) {
    const rootFreq = Tone.Frequency(BASE_NOTES[rootIdx] + (music.octave - 1)).toFrequency();
    const oscVol = -36 + (droneLevel * 12);
    const osc = new Tone.Oscillator({
      type: 'triangle',
      frequency: rootFreq,
      volume: oscVol,
    }).connect(outputGain);
    osc.start();
    return osc;
  }

  /**
   * Create a warm evolving pad layer — adds richness to the planet atmosphere.
   * Uses filtered PolySynth with slow LFO modulating the filter cutoff.
   */
  _createWarmPad(music, rootIdx, outputGain = this.masterGain, droneLevel = 0.5) {
    const freq = Tone.Frequency(BASE_NOTES[rootIdx] + music.octave).toFrequency();
    const fifth = freq * 1.5;
    const octAbove = freq * 2;

    const padVol = -34 + (droneLevel * 12);

    const padFilter = new Tone.Filter({
      frequency: 600 + music.drone * 800,
      type: 'lowpass',
      rolloff: -12,
    }).connect(outputGain);

    const synth = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: 'triangle' },
      envelope: { attack: 6, decay: 3, sustain: 0.5, release: 6 },
      volume: padVol,
    }).connect(padFilter);

    synth.triggerAttack([freq, fifth, octAbove]);

    // Slow filter sweep for evolving texture
    const lfo = new Tone.LFO(0.02 + music.drone * 0.03, 400, 1200).start();
    lfo.connect(padFilter.frequency);

    return { synth, lfo, filter: padFilter };
  }

  /**
   * Create one synth per instrument role so each creature type
   * has a distinct timbre (percussion, keys, brass, synth lead, pad).
   */
  _createInstrumentSynths() {
    const mg = this.masterGain;

    const createSampler = (role, volumeDb) => {
      const gain = new Tone.Gain(0.7).connect(mg);
      const sampler = createRoleSampler(role, volumeDb, gain);
      return { synth: sampler, gain: gain };
    };

    HARMONY.INSTRUMENT_ROLES.forEach(role => {
      // Look up the orchestral section for this instrument to get the right volume
      const section = INSTRUMENT_SECTION_MAP[role.id] || 'rhythm';
      const sectionCfg = ORCHESTRA_SECTIONS[section] || ORCHESTRA_SECTIONS.rhythm;
      const volumeDb = sectionCfg.volumeDb;

      if (role.id === 'percussion') {
        const percGain = new Tone.Gain(0.6).connect(mg);
        const drumKit = createDrumKit(percGain, volumeDb);
        this._instrumentSynths[role.id] = { synth: drumKit, gain: percGain };
      } else {
        this._instrumentSynths[role.id] = createSampler(role, volumeDb);
      }
    });
  }

  _createEffectSynths() {
    this.effectGain = new Tone.Gain(0.75).connect(this.filterNode);

    // Footsteps — tuned membrane drum, plays scale notes
    this.footstepSynth = new Tone.MembraneSynth({
      pitchDecay: 0.04,
      envelope: { attack: 0.001, decay: 0.12, sustain: 0.0, release: 0.1 },
      volume: -14,
    }).connect(this.effectGain);

    // Jump — ascending pitch in key
    this.jumpSynth = new Tone.MembraneSynth({
      pitchDecay: 0.02,
      envelope: { attack: 0.001, decay: 0.18, sustain: 0.0, release: 0.18 },
      volume: -10,
    }).connect(this.effectGain);

    // Boost — bell-like chime
    this.boostSynth = new Tone.MetalSynth({
      harmonicity: 6,
      resonance: 800,
      modulationIndex: 24,
      envelope: { attack: 0.001, decay: 0.15, release: 0.25 },
      volume: -10,
    }).connect(this.effectGain);

    this.jetpackFilter = new Tone.Filter(600, 'lowpass').connect(this.effectGain);
    this.jetpackGain = new Tone.Gain(0).connect(this.jetpackFilter);
    this.jetpackSynth = new Tone.Oscillator({ type: 'triangle', frequency: 180 }).connect(this.jetpackGain);
    this.jetpackSynth.start();

    this.shipEngineFilter = new Tone.Filter(300, 'lowpass').connect(this.effectGain);
    this.shipEngineGain = new Tone.Gain(0).connect(this.shipEngineFilter);
    this.shipEngineSynth = new Tone.Oscillator({ type: 'sine', frequency: 110 }).connect(this.shipEngineGain);
    this.shipEngineSynth.start();
  }

  _bindEventHandlers() {
    this._cleanupEventHandlers();
    this._eventUnsubscribers.push(events.on(EVT.PLAYER_FOOTSTEP, () => this.playFootstep()));
    this._eventUnsubscribers.push(events.on(EVT.PLAYER_JUMP, () => this.playJump()));
    this._eventUnsubscribers.push(events.on(EVT.PLAYER_LAND, () => this.playLanding()));
    this._eventUnsubscribers.push(events.on(EVT.PLAYER_BOOST, () => this.playBoost()));
    this._eventUnsubscribers.push(events.on(EVT.PLAYER_JETPACK_START, () => this.setJetpackActive(true)));
    this._eventUnsubscribers.push(events.on(EVT.PLAYER_JETPACK_STOP, () => this.setJetpackActive(false)));
    this._eventUnsubscribers.push(events.on(EVT.SHIP_THROTTLE, (payload) => this.setShipThrust(payload?.throttle ?? 0, payload?.boost ?? false)));
    this._eventUnsubscribers.push(events.on(EVT.SHIP_BOOST, () => this.playBoost()));
    this._eventUnsubscribers.push(events.on(EVT.CREATURE_CALL, (d) => this.playCreatureCall(d)));
  }

  _cleanupEventHandlers() {
    this._eventUnsubscribers.forEach(fn => fn());
    this._eventUnsubscribers = [];
  }

  playFootstep() {
    if (!this.footstepSynth) return;
    // Play percussive notes from the planet's scale
    const note = this.harmony.getPercussiveNote(this._footstepIndex++);
    this.footstepSynth.triggerAttackRelease(note, '16n');
  }

  playJump() {
    if (!this.jumpSynth) return;
    // Ascending fifth from scale root
    const note = this.harmony.noteFromDegree(4, this.harmony.octave - 1);
    this.jumpSynth.triggerAttackRelease(note, '8n');
  }

  playLanding() {
    if (!this.footstepSynth) return;
    // Root note landing thud
    const note = this.harmony.noteFromDegree(0, this.harmony.octave - 1);
    this.footstepSynth.triggerAttackRelease(note, '16n');
  }

  playBoost() {
    if (!this.boostSynth) return;
    // Chime using a note from the current chord
    const freq = Tone.Frequency(this.harmony.getEffectNote(1, this.harmony.octave + 1)).toFrequency();
    this.boostSynth.triggerAttackRelease(freq, '16n');
  }

  /**
   * Play a creature's note from a bar pattern — triggered by FlockingSystem.
   * Each event carries the instrument role, note, and duration.
   */
  playCreatureCall(data) {
    if (!this.harmony) return;
    const creature = data.creature;
    if (!creature?.voice) return;

    const vol = this._getCreatureSpatialVolume(creature);
    if (vol <= 0.01) return;

    const roleId = creature.voice.instrumentRole || 'flute';
    const inst = this._instrumentSynths[roleId];
    if (!inst) return;

    // Set volume based on spatial distance
    inst.gain.gain.rampTo(Math.min(vol, 0.8), 0.05);

    const note = data.note || this.harmony.getCreatureNote(creature.voice);
    const duration = data.duration || creature.voice.duration || 0.3;

    try {
      inst.synth.triggerAttackRelease(note, duration, undefined, data.velocity ?? 1);
    } catch (_) {}
  }

  /**
   * Compute spatial volume for a creature based on distance to player.
   * Returns 0–1, where 1 = right next to the player, 0 = beyond hearing range.
   */
  _getCreatureSpatialVolume(creature) {
    // Broadcast mode: hear everything at uniform volume
    if (this._broadcastMode) return 0.7;

    if (!this._playerPos || !creature?._meshRef) return 0.6; // fallback if no position data

    const meshPos = creature._meshRef.position;
    const dist = this._playerPos.distanceTo(meshPos);
    if (dist >= this._maxHearDist) return 0;

    // Smooth inverse distance falloff
    const t = dist / this._maxHearDist;
    return Math.pow(1 - t, 1.5) * 0.9; // gentle rolloff
  }

  /**
   * Toggle broadcast mode — hear all creatures on the planet at once.
   * Returns the new state (true = on, false = off).
   */
  toggleBroadcast() {
    this._broadcastMode = !this._broadcastMode;
    return this._broadcastMode;
  }

  /** Get current broadcast mode state */
  get broadcastMode() {
    return this._broadcastMode;
  }

  /** Whether a creature's call is close enough for the player to hear. */
  isCreatureAudible(creature) {
    return this._getCreatureSpatialVolume(creature) > 0.01;
  }

  /**
   * Update the player's position for spatial audio calculations.
   * Call this every frame from the game loop.
   */
  setPlayerPosition(pos) {
    this._playerPos = pos;
  }

  setJetpackActive(active) {
    if (!this.jetpackGain || !this.jetpackFilter) return;
    this.jetpackGain.gain.rampTo(active ? 0.22 : 0, 0.12);
    this.jetpackFilter.frequency.rampTo(active ? 1200 : 300, 0.12);
  }

  setShipThrust(throttle, boost) {
    if (!this.shipEngineGain || !this.shipEngineFilter || !this.shipEngineSynth) return;
    let t = Math.max(0, Math.min(1, throttle || 0));
    if (t < 0.02 && !boost) t = 0;
    const volume = t * 0.0125 + (boost ? 0.0025 : 0);
    this.shipEngineGain.gain.rampTo(volume, 0.14);
    const frequency = 160 + t * 320 + (boost ? 160 : 0);
    this.shipEngineSynth.frequency.rampTo(frequency, 0.16);
    this.shipEngineFilter.frequency.rampTo(frequency, 0.16);
  }

  /**
   * Add a single creature's voice — no-op now since voices are event-driven.
   */
  addCreatureVoice() {
    // Creature sounds are entirely event-driven via FlockingSystem.
    // Nothing to do here.
  }

  async startNearbyPlanetAmbience(planetData) {
    // Nearby planets just get a quiet drone hum, no creature voices
    if (!this.started) await this.init();
    if (!this.nearbyGain) return;
    const planetIndex = planetData.index;
    if (this.nearbyPlanetVoices.some(v => v.planetIndex === planetIndex)) return;

    const { music } = planetData;
    const rootIdx = BASE_NOTES.indexOf(music.root);
    const freq = Tone.Frequency(BASE_NOTES[rootIdx] + (music.octave - 1)).toFrequency();

    const synth = new Tone.Synth({
      oscillator: { type: 'sine' },
      envelope: { attack: 3, decay: 1, sustain: 0.6, release: 3 },
      volume: -35,
    }).connect(this.nearbyGain);

    synth.triggerAttack(Tone.Frequency(freq).toNote());

    this.nearbyPlanetVoices.push({ synth, planetIndex });
    Tone.getTransport().start();
  }

  stopNearbyPlanetAmbience(planetIndex) {
    const keep = [];
    this.nearbyPlanetVoices.forEach(v => {
      if (v.planetIndex === planetIndex) {
        try {
          if (v.loop) v.loop.stop();
          v.synth.triggerRelease();
          if (v.loop) this.scheduledEvents = this.scheduledEvents.filter(evt => evt !== v.loop);
          setTimeout(() => v.synth.dispose(), 2000);
        } catch (_) {}
      } else {
        keep.push(v);
      }
    });
    this.nearbyPlanetVoices = keep;
  }

  /**
   * Remove a creature's voice (when collected) — no per-creature synth to clean up.
   */
  removeCreatureVoice() {
    // No per-creature synths to dispose — voices are pooled and event-driven.
    // FlockingSystem.removeCreature() handles the flock side.
  }

  /** Fade the music for warping */
  fadeOut(duration = 2) {
    if (this.masterGain) {
      this.masterGain.gain.rampTo(0, duration);
    }
  }

  fadeIn(duration = 2) {
    if (this.masterGain) {
      this.masterGain.gain.rampTo(0.6, duration);
    }
  }

  /** Ramp master volume to an explicit level (facade over Tone internals). */
  fadeTo(level, duration = 2) {
    if (this.masterGain) {
      this.masterGain.gain.rampTo(level, duration);
    }
  }

  /**
   * Smoothly set the master gain based on distance from planet.
   * @param {number} t  0 = at surface (full volume), 1 = far away (silent)
   */
  setDistanceFade(t) {
    if (!this.masterGain) return;
    const baseVol = Math.max(0, Math.min(0.6, 0.6 * (1 - t)));
    const vol = this._broadcastMode ? Math.max(baseVol, 0.45) : baseVol;
    this.masterGain.gain.rampTo(vol, 0.15);
  }

  stopAll() {
    Tone.getTransport().stop();
    Tone.getTransport().cancel();

    this.scheduledEvents.forEach(e => { try { e.stop(); e.dispose(); } catch(_){} });
    this.scheduledEvents = [];

    this.nearbyPlanetVoices.forEach(v => {
      try {
        if (v.loop) v.loop.stop();
        v.synth.triggerRelease();
        setTimeout(() => v.synth.dispose(), 2000);
      } catch (_) {}
    });
    this.nearbyPlanetVoices = [];

    if (this.currentPlanetSymphony) {
      this._disposePlanetSymphony(this.currentPlanetSymphony);
      this.currentPlanetSymphony = null;
    }
  }

  /**
   * Play back a saved MusicMaker composition — used by the planet radio in the endgame.
   * Mirrors MusicMaker's own playback exactly: same scale-note mapping, same 16n timing.
   * @param {object} songData  Deserialized composition: { tracks, bpm, key, scaleName }
   */
  playRadioSong(songData) {
    if (!this.started || !this.masterGain) return;
    this.stopRadioSong();

    // Shared format expansion — handles v1/v2 notes, real grid size, octave
    const song = expandSong(songData);
    if (!song) return;

    // Dedicated gain for the radio — fades in independently of the planet symphony
    const radioGain = new Tone.Gain(0).connect(this.masterGain);

    // Create one synth per track, matching MusicMaker's instrument assignment
    // (0.625 × volume mirrors the editor's track-gain curve)
    const synths = song.tracks.map(track => {
      const role = HARMONY.INSTRUMENT_ROLES.find(r => r.id === track.instrumentRole);
      const trackGain = 0.625 * (track.volume ?? 0.8);

      if (track.instrumentRole === 'percussion') {
        const gain = new Tone.Gain(trackGain).connect(radioGain);
        const synth = createDrumKit(gain, -8);
        return { synth, gain, isPerc: true };
      } else if (role?.sampleMapping) {
        const gain = new Tone.Gain(trackGain).connect(radioGain);
        const synth = createRoleSampler(role, -6, gain);
        return { synth, gain, isPerc: false };
      } else {
        const gain = new Tone.Gain(trackGain).connect(radioGain);
        const synth = new Tone.PolySynth(Tone.Synth, {
          oscillator: { type: 'triangle' },
          envelope: { attack: 0.02, decay: 0.2, sustain: 0.3, release: 0.4 },
          volume: -8,
        }).connect(gain);
        return { synth, gain, isPerc: false };
      }
    });

    Tone.getTransport().bpm.value = song.bpm;

    // Pre-index events per track per step
    const stepEvents = song.tracks.map(t => {
      const m = new Map();
      for (const ev of t.events) {
        if (!m.has(ev.step)) m.set(ev.step, []);
        m.get(ev.step).push(ev);
      }
      return m;
    });

    const cols = Array.from({ length: song.cols }, (_, i) => i);
    const sequence = new Tone.Sequence((time, col) => {
      for (let ti = 0; ti < stepEvents.length; ti++) {
        const evs = stepEvents[ti].get(col);
        const synthInfo = synths[ti];
        if (!evs || !synthInfo) continue;
        for (const ev of evs) {
          try {
            if (synthInfo.isPerc) {
              synthInfo.synth.triggerAttackRelease(ev.note, '16n', time, ev.vel ?? 1);
            } else {
              // Honor the drawn note length
              synthInfo.synth.triggerAttackRelease(ev.note, ev.durSteps * song.stepDur * 0.95, time, ev.vel ?? 1);
            }
          } catch (_) {}
        }
      }
    }, cols, '16n');

    sequence.start(0);
    radioGain.gain.rampTo(0.8, 3);
    Tone.getTransport().start();

    this._radioSong = { sequence, synths, gain: radioGain };
    console.log('[Audio] Planet radio playing:', song.bpm, 'bpm,', song.cols, 'steps');
  }

  /** Stop the planet radio playback cleanly. */
  stopRadioSong() {
    if (!this._radioSong) return;
    const { sequence, synths, gain } = this._radioSong;
    try { sequence.stop(); sequence.dispose(); } catch (_) {}
    gain.gain.rampTo(0, 1.5);
    setTimeout(() => {
      for (const s of synths) {
        try { s.synth.dispose(); } catch (_) {}
        try { s.gain.dispose(); } catch (_) {}
      }
      try { gain.dispose(); } catch (_) {}
    }, 2000);
    this._radioSong = null;
  }

  dispose() {
    this._cleanupEventHandlers();

    // Dispose instrument synths
    for (const [, inst] of Object.entries(this._instrumentSynths)) {
      try { inst.synth.dispose(); } catch (_) {}
      try { inst.gain.dispose(); } catch (_) {}
      if (inst.noiseSynth) try { inst.noiseSynth.dispose(); } catch (_) {}
    }
    this._instrumentSynths = {};

    if (this.masterGain) this.masterGain.dispose();
    if (this.effectGain) this.effectGain.dispose();
    if (this.footstepSynth) this.footstepSynth.dispose();
    if (this.jumpSynth) this.jumpSynth.dispose();
    if (this.boostSynth) this.boostSynth.dispose();
    if (this.jetpackSynth) this.jetpackSynth.stop();
    if (this.jetpackGain) this.jetpackGain.dispose();
    if (this.jetpackFilter) this.jetpackFilter.dispose();
    if (this.shipEngineSynth) this.shipEngineSynth.stop();
    if (this.shipEngineGain) this.shipEngineGain.dispose();
    if (this.shipEngineFilter) this.shipEngineFilter.dispose();
    if (this.nearbyGain) this.nearbyGain.dispose();
    if (this.reverbNode) this.reverbNode.dispose();
    if (this.chorusNode) this.chorusNode.dispose();
    if (this.delayNode) this.delayNode.dispose();
    if (this.filterNode) this.filterNode.dispose();
  }
}
